<div class="insp">

  <header class="insp__head">
    <div>
      <h1 class="insp__title">
        <i class="fa-solid fa-clipboard-check"></i>
        {{ 'INSPECTION.TITLE' | translate }}
      </h1>
      <p class="insp__sub">{{ 'INSPECTION.SUBTITLE' | translate }}</p>
    </div>
    <span class="insp__count">{{ 'INSPECTION.COUNT' | translate:{ count: total() } }}</span>
  </header>

  <!-- Ce que le serveur laisse voir : le périmètre, ou son absence. -->
  @if (scope(); as s) {
    <app-inspection-scope-summary class="insp__scope" [scope]="s"></app-inspection-scope-summary>
  }

  <!-- ─── Facettes ─────────────────────────────────────────────────────
       L'état ouvre sur « à inspecter » : c'est la liste de travail. Ouvrir
       sur l'ensemble obligerait à filtrer avant de commencer, à chaque
       visite. -->
  <div class="insp__filters">
    <label class="insp-filter">
      <span class="insp-filter__label">{{ 'INSPECTION.FILTER.STATE' | translate }}</span>
      <select [(ngModel)]="inspectedFilter" (ngModelChange)="applyFilters()">
        <option value="no">{{ 'INSPECTION.FILTER.TODO' | translate }}</option>
        <option value="yes">{{ 'INSPECTION.FILTER.DONE' | translate }}</option>
        <option value="all">{{ 'INSPECTION.FILTER.ALL' | translate }}</option>
      </select>
    </label>

    <label class="insp-filter">
      <span class="insp-filter__label">{{ 'CALENDAR.FILTER.PROFESSOR' | translate }}</span>
      <select [(ngModel)]="filterProfessorId" (ngModelChange)="applyFilters()">
        <option [ngValue]="null">{{ 'INSPECTION.FILTER.ALL_PROFESSORS' | translate }}</option>
        @for (p of options().professors; track p.id) {
          <option [ngValue]="p.id">{{ p.fullName }}</option>
        }
      </select>
    </label>

    <label class="insp-filter">
      <span class="insp-filter__label">{{ 'CALENDAR.FILTER.SUBJECT' | translate }}</span>
      <select [(ngModel)]="filterSubjectId" (ngModelChange)="applyFilters()">
        <option [ngValue]="null">{{ 'CALENDAR.FILTER.ALL_SUBJECTS' | translate }}</option>
        @for (g of subjectGroups(); track g.pathId) {
          <optgroup [label]="g.pathLabel || ('CALENDAR.CREATE.NO_PATH' | translate)">
            @for (s of g.subjects; track s.id) {
              <option [ngValue]="s.id">{{ subjectLabel(s) }}</option>
            }
          </optgroup>
        }
      </select>
    </label>

    <label class="insp-filter">
      <span class="insp-filter__label">{{ 'INSPECTION.PERIOD.LABEL' | translate }}</span>
      <select [(ngModel)]="period" (ngModelChange)="applyFilters()">
        <option value="month">{{ 'INSPECTION.PERIOD.MONTH' | translate }}</option>
        <option value="all">{{ 'INSPECTION.PERIOD.ALL' | translate }}</option>
        <option value="custom">{{ 'INSPECTION.PERIOD.CUSTOM' | translate }}</option>
      </select>
    </label>

    @if (period === 'custom') {
      <label class="insp-filter">
        <span class="insp-filter__label">{{ 'INSPECTION.FILTER.FROM' | translate }}</span>
        <input type="date" [(ngModel)]="filterFrom" (ngModelChange)="applyFilters()">
      </label>

      <label class="insp-filter">
        <span class="insp-filter__label">{{ 'INSPECTION.FILTER.TO' | translate }}</span>
        <input type="date" [(ngModel)]="filterTo" (ngModelChange)="applyFilters()">
      </label>
    }

    @if (hasFilters) {
      <button type="button" class="insp__clear" (click)="clearFilters()">
        <i class="fa-solid fa-xmark"></i>
        {{ 'CALENDAR.FILTER.CLEAR' | translate }}
      </button>
    }
  </div>

  @if (joinError()) {
    <p class="insp__alert" role="alert">
      <i class="fa-solid fa-circle-exclamation"></i>
      <span>{{ joinError()! | translate }}</span>
    </p>
  }

  <!-- ─── Liste ────────────────────────────────────────────────────── -->
  @if (errored()) {
    <app-error-state titleKey="INSPECTION.ERROR_TITLE"
                     messageKey="INSPECTION.ERROR_DESC"
                     (retry)="reload()"></app-error-state>
  } @else if (!loading() && scopeIsEmpty()) {
    <!-- Le résumé du périmètre, au-dessus, dit déjà pourquoi la liste est vide. -->
  } @else if (!loading() && sessions().length === 0) {
    <app-empty-state icon="fa-solid fa-clipboard-check"
                     titleKey="INSPECTION.EMPTY_TITLE"
                     messageKey="INSPECTION.EMPTY_DESC"></app-empty-state>
  } @else {
    <div class="insp__table-wrap">
      <table class="insp-table">
        <thead>
          <tr>
            <th>{{ 'INSPECTION.COL.SESSION' | translate }}</th>
            <th>{{ 'INSPECTION.COL.WHEN' | translate }}</th>
            <th>{{ 'INSPECTION.COL.PROFESSOR' | translate }}</th>
            <th>{{ 'INSPECTION.COL.SUBJECT' | translate }}</th>
            <th>{{ 'INSPECTION.COL.STATE' | translate }}</th>
            <th class="insp-table__actions">{{ 'INSPECTION.COL.ACTIONS' | translate }}</th>
          </tr>
        </thead>
        <tbody>
          @for (s of sessions(); track s.id) {
            <tr [class.is-inspected]="s.isInspected" [class.is-ongoing]="s.isOngoing">
              <td>
                <span class="insp-table__title">{{ s.title }}</span>
                <!-- Le niveau d'abord, toujours : le groupe manque à une séance individuelle. -->
                @if (levelsOf(s); as level) {
                  <span class="insp-table__dim insp-table__line"><i class="fa-solid fa-layer-group"></i>{{ level }}</span>
                }
                @if (s.groupName) {
                  <span class="insp-table__dim insp-table__line"><i class="fa-solid fa-users"></i>{{ s.groupName }}</span>
                }
              </td>
              <td>
                {{ s.scheduledDate | date:'dd/MM/yyyy' }}
                <span class="insp-table__dim">{{ s.durationMinutes }}′</span>
              </td>
              <td>{{ s.consultantName }}</td>
              <td>{{ subjectsOf(s) }}</td>
              <td>
                @if (s.isOngoing) {
                  <span class="insp-tag insp-tag--live">
                    <i class="fa-solid fa-circle-dot"></i>
                    {{ 'INSPECTION.STATE.ONGOING' | translate }}
                  </span>
                }
                @if (s.isInspected) {
                  <span class="insp-tag insp-tag--done" [title]="inspectorsOf(s)">
                    <i class="fa-solid fa-circle-check"></i>
                    {{ 'INSPECTION.STATE.DONE' | translate }}
                  </span>
                } @else {
                  <span class="insp-tag insp-tag--todo">
                    <i class="fa-solid fa-clock"></i>
                    {{ 'INSPECTION.STATE.TODO' | translate }}
                  </span>
                }
              </td>
              <td class="insp-table__actions">
                <!-- Tant que la séance se tient, on la rejoint : l'entrée est
                     journalisée et atteste l'inspection en direct. Une fois
                     terminée, le lien a expiré chez le fournisseur — proposer
                     d'y entrer mènerait à une page d'erreur. L'enregistrement
                     prend alors le relais. -->
                @if (rules.canJoin(s)) {
                  <button type="button" class="insp-btn" (click)="join(s)">
                    <i class="fa-solid fa-video"></i>
                    {{ 'INSPECTION.JOIN' | translate }}
                  </button>
                } @else {
                  <!-- `as` n'est admis que sur le @if primaire : d'où ce bloc
                       imbriqué plutôt qu'un @else if. -->
                  @if (rules.playable(s); as rec) {
                    <button type="button" class="insp-btn" (click)="openRecording(s)"
                            [title]="rec.title">
                      <i class="fa-solid fa-circle-play"></i>
                      {{ 'INSPECTION.WATCH' | translate }}
                      @if (s.recordingCount > 1) {
                        <span class="insp-table__dim">({{ s.recordingCount }})</span>
                      }
                    </button>
                  } @else if (rules.ended(s) && s.hasMeeting) {
                    <span class="insp-table__dim">{{ 'INSPECTION.LINK_EXPIRED' | translate }}</span>
                  }
                }

                @if (!rules.inspectable(s)) {
                  <span class="insp-table__dim">{{ 'INSPECTION.NOT_YET' | translate }}</span>
                }
                <button type="button" class="insp-btn insp-btn--primary" (click)="openReport(s)">
                  <i class="fa-solid fa-pen-to-square"></i>
                  {{ (s.isInspected ? 'INSPECTION.EDIT_REPORT' : 'INSPECTION.WRITE_REPORT') | translate }}
                </button>
              </td>
            </tr>
          }
        </tbody>
      </table>
    </div>

    <div class="insp__pager">
      <button type="button" class="insp-btn" [disabled]="!canPrevious()" (click)="previous()">
        <i class="fa-solid fa-chevron-left"></i>
      </button>
      <span>{{ page() }} / {{ totalPages() }}</span>
      <button type="button" class="insp-btn" [disabled]="!canNext()" (click)="next()">
        <i class="fa-solid fa-chevron-right"></i>
      </button>
    </div>
  }

  <!-- ─── Rapport : le dialogue partagé ─────────────────────────────── -->
  <app-inspection-report-dialog [session]="target()"
                                (saved)="onReportSaved()"
                                (changed)="load()"
                                (closed)="target.set(null)"></app-inspection-report-dialog>

  <!-- L'enregistrement se regarde ici : lecteur sans téléchargement. -->
  <app-material-preview [item]="preview()" (closed)="preview.set(null)"></app-material-preview>
</div>
